import { LangSwitcher } from '@/components/i18n/LangSwitcher';
import { SourceStacker } from '@/components/inputs/SourceStacker';
import { InstallButton } from '@/components/pwa/InstallButton';
import { useT } from '@/lib/i18n';
import { navigate } from '@/lib/nav';
import { ocrImage } from '@/lib/ocr/tesseract';
import { openPdf } from '@/lib/pdf/extract';
import { useSourceStore } from '@/lib/state/sourceStore';
import { useCallback, useEffect, useState } from 'react';

export function LandingRoute() {
  const { t } = useT();
  const addSource = useSourceStore((s) => s.addSource);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  /** Handle dropped PDFs (text extract) or images (OCR). */
  const ingestFile = useCallback(
    async (file: File) => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isImage = file.type.startsWith('image/');

      if (isPdf) {
        setBusy(`Lecture de ${file.name}…`);
        try {
          const doc = await openPdf(file);
          const lo = 1;
          const hi = Math.min(doc.pageCount, 10);
          const text = await doc.extractText(lo, hi);
          await doc.destroy().catch(() => undefined);
          if (text.trim()) {
            addSource({
              kind: 'pdf',
              label: file.name,
              text,
              page_range: [lo, hi],
              mime_type: 'application/pdf',
              byte_size: file.size,
            });
          }
        } finally {
          setBusy(null);
        }
        return;
      }

      if (isImage) {
        setBusy(`OCR de ${file.name}…`);
        try {
          const res = await ocrImage(file);
          if (res.text.trim()) {
            addSource({
              kind: 'image',
              label: file.name,
              text: res.text,
              mime_type: file.type,
              byte_size: file.size,
            });
          }
        } finally {
          setBusy(null);
        }
        return;
      }
    },
    [addSource],
  );

  // Whole-page drag & drop. Prevents the browser from navigating away when a
  // file is dropped on empty area.
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        e.preventDefault();
        setDragOver(true);
      }
    };
    const onDragLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) setDragOver(false);
    };
    const onDrop = async (e: DragEvent) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      for (const f of files) {
        await ingestFile(f);
      }
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [ingestFile]);

  const handleGenerate = () => {
    if (useSourceStore.getState().sources.length === 0) return;
    navigate('/generate');
  };

  return (
    <main
      data-testid="landing-root"
      className="min-h-screen px-4 py-8"
      style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(255,200,140,.18), transparent 40%), radial-gradient(circle at 80% 70%, rgba(140,180,255,.15), transparent 45%), linear-gradient(#ece5d2, #e0d8c0)',
      }}
    >
      <div
        className={[
          'relative max-w-3xl mx-auto rounded-card px-6 md:px-10 py-8 md:py-10',
          dragOver ? 'ring-4 ring-accent-blue/60' : '',
        ].join(' ')}
        style={{
          background: '#fdfaf1',
          backgroundImage:
            'linear-gradient(#e7dec8 1px, transparent 1px), linear-gradient(90deg, #e7dec8 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          backgroundPosition: '-1px -1px',
          border: '2.5px solid #1f2330',
          boxShadow:
            '0 1px 0 rgba(0,0,0,.04), 0 12px 28px rgba(60,40,20,.18), 0 30px 60px rgba(60,40,20,.12)',
        }}
      >
        <div className="absolute top-3 right-3 sm:right-4 z-10">
          <LangSwitcher />
        </div>
        <header className="flex flex-col items-center gap-2 mb-6 mt-7 sm:mt-0 relative">
          <div
            className="bg-accent-yellow border-[2.5px] border-ink rounded-card px-5 sm:px-6 py-2 shadow-ink-lg font-gloock text-[26px] xs:text-[30px] sm:text-3xl md:text-4xl tracking-wide max-w-full"
            style={{ transform: 'rotate(-1deg)' }}
          >
            {t('landing.title')}
          </div>
          <p className="font-caveat text-xl xs:text-2xl text-ink-soft text-center">
            {t('landing.tagline')}
          </p>
          <div className="flex gap-4 font-caveat text-[16px] mt-1">
            <a href="/sample" className="text-accent-blue underline-offset-4 hover:underline">
              {t('landing.sample_link')}
            </a>
            <a
              href="/library"
              className="text-accent-blue underline-offset-4 hover:underline"
              data-testid="library-link"
            >
              {t('landing.library_link')}
            </a>
          </div>
          <div className="mt-2">
            <InstallButton />
          </div>
        </header>

        <SourceStacker onGenerate={handleGenerate} />

        {dragOver ? (
          <div
            className="pointer-events-none absolute inset-0 rounded-card flex items-center justify-center"
            style={{ background: 'rgba(46,108,181,0.08)' }}
            aria-hidden
          >
            <span className="font-gloock text-2xl text-accent-blue bg-paper border-[2.5px] border-ink rounded-card px-5 py-2 shadow-ink">
              {t('landing.dropzone_drop')}
            </span>
          </div>
        ) : null}

        {busy ? (
          <output className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-ink text-paper font-hand text-[15px] px-4 py-2 rounded-card shadow-lg">
            ⏳ {busy}
          </output>
        ) : null}
      </div>
      <p className="text-center mt-6 font-caveat text-ink-soft text-lg">{t('landing.footer')}</p>
    </main>
  );
}
